import React, {
    useState,
    useMemo,
    useRef,
    useCallback,
    useEffect
} from "react";

import { groupBy, uniq, uniqBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Typography, Button } from "@mui/material/";
import actions from "../../Store/actions";
import { strings } from "../../common/i18n";
import { helpers } from "../../common/lib";
import { CsvError } from ".././components";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { v4 as uuidv4 } from "uuid";
import { logToSentry } from "../../config/Sentry";
import styles from "../styles";

/**
 *  Hook for location table data and operations
 */

export default () => {
    const { branches, branchLocations, locations, userOrganisations } =
        useSelector((state) => state);

    const dispatch = useDispatch();
    const [optionOpen, setOptionsOpen] = useState(null);
    const [error, setError] = useState(false);
    const [loc, setLoc] = useState(null);
    const locationNamesRef = useRef({});

    /** On menu open close */
    const onMenuClose = () => setOptionsOpen(null);
    const onMenuOpen = (event) => setOptionsOpen(event.currentTarget);

    /** Columns  */
    const columns = useMemo(
        () => [
            {
                id: "name",
                header: strings("name"),
                accessorKey: "name",
                hideable: false,
                filterVariant: "autocomplete",

                size: 150
            },
            {
                id: "locationRef",
                header: strings("locationRef"),
                accessorKey: "locationRef",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            },
            {
                id: "branchName",
                header: strings("branchName"),
                accessorKey: "branchName",
                filterVariant: "autocomplete",
                size: 150
            },
            {
                id: "branchRef",
                header: strings("branchRef"),
                accessorKey: "branchRef",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            },
            {
                id: "latitude",
                header: strings("latitude"),
                accessorKey: "latitude",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            },
            {
                id: "longitude",
                header: strings("longitude"),
                accessorKey: "longitude",
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150
            }
        ],
        []
    );

    /** Row Actions */
    const rowActions = (row) => {
        const { name } = row.original;
        return [
            name && (
                <IconButton
                    key="edit"
                    title={strings("edit")}
                    onClick={() => onDataItemSet(row.original)}
                >
                    <EditIcon color="primary" />
                </IconButton>
            )
        ];
    };

    const branchIdRefMap = useMemo(
        () => helpers.idNameMap(branches.data, "branchRef"),
        [branches]
    );

    /** Rows data */
    const data = useMemo(() => {
        let next = [];
        const byLocation = groupBy(branchLocations.data, "location");
        next = locations.data.map(({ id, locationRef, name, geo }) => {
            const location = {
                id: id ? id : uuidv4(),
                branches: [],
                branchName: "",
                branchRef: "",
                locationRef: helpers.nameJoin(locationRef),
                name,
                latitude: geo && geo.length > 0 ? `${geo[0].latitude}` : null,
                longitude: geo && geo.length > 0 ? `${geo[0].longitude}` : null
            };
            const branchRefs = [];
            uniqBy(byLocation[id], "branch").forEach(({ branch }) => {
                const index = branches.byId[branch];
                if (index != null) {
                    location.branches.push(branches.data[index]);
                    const { branchRef } = branches.data[index];
                    branchRefs.push(...branchRef);
                    location.branchName = branches.data[index].name;
                }
            });
            location.branchRef = helpers.nameJoin(branchRefs);
            return location;
        });
        return next;
    }, [branches, branchLocations, locations]);

    const onCsvMap = useCallback(
        ({ branchRef, locationRef, name, row }) => {
            const next = {
                branches: [],
                branchRef,
                errors: [],
                locationRef,
                name
            };

            /** Commenting for DS
             * Same location name can be added in locations
             */
            // if (locationNamesRef.current[name]) {
            //     next.errors.push(
            //         <CsvError
            //             error={`${strings("errors.fieldUnique")} [${name}]`}
            //             row={row}
            //         />
            //     );
            //     setError(true);
            // } else {
            //     locationNamesRef.current[name] = true;
            // }

            if (branchRef !== null) {
                helpers.nameSplit(branchRef).forEach(({ name }) => {
                    const branchId = branchIdRefMap[name];
                    if (branchId == null) {
                        next.errors.push(
                            <CsvError
                                error={`${strings(
                                    "errors.refInvalid"
                                )} [${name}]`}
                                row={row}
                            />
                        );
                        setError(true);
                    } else {
                        next.branches = uniq([...next.branches, branchId]);
                    }
                });
            } else {
                next.errors.push(
                    <CsvError
                        key={`${row}_ref`}
                        error={`[Branch Reference] ${strings(
                            "errors.fieldRequired"
                        )} `}
                        row={row}
                    />
                );
                setError(true);
            }

            return next;
        },
        [branches, branchIdRefMap]
    );

    const onCsvUpload = () => {
        locationNamesRef.current = {};
        setError(false);
    };

    const onDataItemAdd = () => dispatch(actions.locations.create.itemAdd());

    const onDataItemClear = async () => {
        dispatch(actions.locations.create.itemClear());
    };

    const currentLocation = useMemo(
        () => loc && locations.data.find((item) => item.id === loc.id),
        [locations, loc]
    );

    useEffect(() => {
        // Dispatch the action when currentRow changes
        if (currentLocation) {
            dispatch(actions.locations.create.itemSet(currentLocation));
            setLoc(null);
        }
    }, [dispatch, currentLocation]);

    const onDataItemSet = useCallback(
        (row) => {
            setLoc(row);
        },
        [setLoc, dispatch, currentLocation]
    );

    const onDataSave = async (next: Array<any>) => {
        try {
            const nextLocations = [];
            next.forEach(({ branches, locationRef, name }) =>
                nextLocations.push({
                    branch: branches,
                    locationRef: helpers.nameSplit(locationRef),
                    name,
                    organisation: userOrganisations.item.id
                })
            );
            await dispatch(actions.locations.create.postRequest(nextLocations));
            await dispatch(actions.locations.create.getRequest());
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On location data save:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onDataSaveLocation"))
            );
        }
    };
    /** Toolbar components for search  */
    const toolBarComponents = () => (
        <>
            <IconButton
                key="edit"
                title={strings("options")}
                onClick={onMenuOpen}
            >
                <MoreHorizIcon color="primary" />
            </IconButton>
            <Button
                sx={styles.primaryButton}
                variant="contained"
                size="small"
                color="primary"
                onClick={onDataItemAdd}
                endIcon={<AddIcon />}
            >
                {strings("addLocation")}
            </Button>
        </>
    );

    return {
        columns,
        data,
        rowActions,
        error,
        onCsvMap,
        onCsvUpload,
        onDataItemAdd,
        onDataItemClear,
        onDataItemSet,
        onDataSave,
        optionOpen,
        onMenuOpen,
        onMenuClose,
        toolBarComponents
    };
};
